import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import GoogleIcon from '@mui/icons-material/Google';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../services/firebase';
import { Google } from '@mui/icons-material';

interface AuthDialogProps {
  open: boolean;
  onClose: () => void;
  onShowRegister: () => void;
}

const AuthDialog: React.FC<AuthDialogProps> = ({ open, onClose, onShowRegister }) => {
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [isLogin, setIsLogin] = useState(true);
  const { handleEmailLogin, handleRegister, loading, error, handleGoogleLogin } = useAuth();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFormData({ email: '', password: '', name: '' });
      setIsLogin(true);
    }
    console.log('AuthDialog open:', open, 'isLogin:', isLogin);
  }, [open]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isLogin) {
        await handleEmailLogin(formData.email, formData.password);
      } else {
        await handleRegister(formData.email, formData.password, formData.name);
      }
      onClose();
    } catch (error) {
      console.error(isLogin ? '登入失敗:' : '註冊失敗:', error);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setResetMsg(null);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMsg('已寄出重設密碼信件，請查收 Email');
    } catch (error: any) {
      setResetMsg(error.message || '寄送失敗，請確認 Email 是否正確');
    } finally {
      setResetLoading(false);
    }
  };

  useEffect(() => {
    console.log('AuthDialog open:', open);
  }, [open]);

  useEffect(() => {
    if (resetMsg && resetMsg.includes('已寄出')) {
      const timer = setTimeout(() => {
        setShowResetDialog(false);
        setResetMsg(null);
        setResetEmail('');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [resetMsg]);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="xs" 
        PaperProps={{
          style: {
            width: '360px',
            maxWidth: '95vw',
            borderRadius: 20,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            padding: 0
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 800, fontSize: 26, color: '#1976D2', letterSpacing: 1, pt: 4, pb: 1 }}>
          登入 <span style={{ color: '#FF0000' }}>CheckPlay</span> 簡播
        </DialogTitle>
        <DialogContent sx={{ px: 5, pt: 0, pb: 2 }}>
          {/* Google 登入按鈕放最上方 */}
          {isLogin && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon sx={{ color: '#EA4335', fontSize: 28 }} />}
              sx={{
                mt: 2,
                mb: 2,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: 17,
                color: '#444',
                borderColor: '#EA4335',
                '&:hover': { borderColor: '#EA4335', background: '#fbe9e7' }
              }}
              onClick={async (e) => {
                e.preventDefault();
                
                // 🔧 修復：防止重複點擊
                if (loading) {
                  console.log('🔐 登入操作進行中，跳過重複點擊');
                  return;
                }
                
                console.log('🔐 Google 登入按鈕被點擊');
                
                // 🚀 優化：點擊登入按鈕時立即開始預載入
                // 🔧 改善：添加超時和錯誤處理，確保不會拖慢登入速度
                const startPreloading = () => {
                  console.log('🚀 開始背景預載入數據...');
                  
                  // 🔧 優化：設置超時時間
                  const fetchWithTimeout = (url: string, timeout = 3000) => {
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), timeout);
                    
                    return fetch(url, { signal: controller.signal })
                      .then(response => {
                        clearTimeout(timeoutId);
                        return response;
                      })
                      .catch(error => {
                        clearTimeout(timeoutId);
                        throw error;
                      });
                  };
                  
                  // 預載入社群影片（不需要認證）
                  fetchWithTimeout('/api/community-public-videos')
                    .then(async response => {
                      if (!response.ok) return null;
                      // 🔧 修復：檢查 Content-Type，避免 HTML 被當作 JSON 解析
                      const contentType = response.headers.get('content-type');
                      if (contentType && contentType.includes('application/json')) {
                        return response.json();
                      }
                      return null;
                    })
                    .then(data => {
                      if (data?.videos) {
                        console.log('✅ 預載入社群影片完成:', data.videos.length, '個影片');
                      }
                    })
                    .catch(error => {
                      // 靜默處理，不輸出詳細錯誤
                      if (error.name !== 'AbortError') {
                        console.warn('⚠️ 預載入社群影片跳過（頁面切換或網絡問題）');
                      }
                    });
                  
                  // 預載入專家標籤
                  fetchWithTimeout('/api/expert-tags')
                    .then(async response => {
                      if (!response.ok) return null;
                      // 🔧 修復：檢查 Content-Type，避免 HTML 被當作 JSON 解析
                      const contentType = response.headers.get('content-type');
                      if (contentType && contentType.includes('application/json')) {
                        return response.json();
                      }
                      return null;
                    })
                    .then(data => {
                      if (data?.tags) {
                        console.log('✅ 預載入專家標籤完成:', data.tags.length, '個標籤');
                      }
                    })
                    .catch(error => {
                      // 靜默處理，不輸出詳細錯誤
                      if (error.name !== 'AbortError') {
                        console.warn('⚠️ 預載入專家標籤跳過（頁面切換或網絡問題）');
                      }
                    });
                };
                
                // 🔧 優化：在背景執行預載入，不阻塞登入流程
                startPreloading();
                
                try {
                  console.log('🔐 開始調用 handleGoogleLogin');
                  await handleGoogleLogin();
                  console.log('🔐 Google 登入成功，關閉對話框');
                  onClose();
                } catch (err) {
                  console.error('🔐 Google 登入失敗:', err);
                  // 🔧 修復：登入失敗時不關閉對話框，讓用戶重試
                  alert('Google 登入失敗，請檢查 Firebase 配置或稍後再試');
                  // 不調用 onClose()，保持對話框打開
                }
              }}
              disabled={loading}
            >
              使用 Google ID 登入
            </Button>
          )}
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              用Google登入較快
            </Typography>
          </Divider>
          {/* Email/密碼表單放在 Divider 下方 */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 0 }}>
            {!isLogin && (
              <TextField
                fullWidth
                label="姓名"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                margin="normal"
                required
                onFocus={e => e.target.select()}
              />
            )}
            <TextField
              fullWidth
              label="電子郵件"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleFormChange}
              margin="normal"
              required
              autoComplete="email"
              onFocus={e => e.target.select()}
            />
            <TextField
              fullWidth
              label="密碼"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleFormChange}
              margin="normal"
              required
              autoComplete="current-password"
              onFocus={e => e.target.select()}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ mt: 3, mb: 2 }} />
            <DialogActions sx={{ mt: 2, flexDirection: 'row', alignItems: 'center', gap: 2 }}>
              <Button onClick={onClose} color="primary" fullWidth sx={{ borderRadius: 2, fontWeight: 700, fontSize: 16, px: 4, py: 1 }}>
                取消
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ fontSize: 18, borderRadius: 2, fontWeight: 800, py: 1.2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : (isLogin ? '登入' : '註冊')}
              </Button>
            </DialogActions>
          </Box>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            {isLogin ? (
              <Typography variant="body2">
                還沒有CheckPlay帳號？{' '}
                <Button variant="text" onClick={() => {
                  console.log('開啟註冊 Dialog');
                  onShowRegister();
                }} size="small">註冊</Button>
                <span style={{ marginLeft: 8 }}>
                  <Button variant="text" size="small" onClick={() => setShowResetDialog(true)}>
                    忘記密碼？
                  </Button>
                </span>
              </Typography>
            ) : (
              <Typography variant="body2">
                已經有帳號？{' '}
                <Button variant="text" onClick={() => setIsLogin(true)} size="small">登入</Button>
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      {/* 忘記密碼 Dialog */}
      <Dialog open={showResetDialog} onClose={() => { setShowResetDialog(false); setResetMsg(null); }} maxWidth="xs">
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, fontSize: 22 }}>重設密碼</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>請輸入註冊時的 Email，我們會寄送重設密碼連結給你。</Typography>
          <Box component="form" onSubmit={handleResetPassword}>
            <TextField
              fullWidth
              label="電子郵件"
              type="email"
              value={resetEmail}
              onChange={e => setResetEmail(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            {resetMsg && <Alert severity={resetMsg.includes('已寄出') ? 'success' : 'error'} sx={{ mb: 2 }}>{resetMsg}</Alert>}
            <DialogActions>
              <Button onClick={() => { setShowResetDialog(false); setResetMsg(null); }} color="primary">取消</Button>
              <Button type="submit" variant="contained" color="primary" disabled={resetLoading}>
                {resetLoading ? <CircularProgress size={20} /> : '送出'}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AuthDialog; 